import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Download } from "lucide-react";
import { LESSON_TYPE_META } from "./LessonDialog";

export default function LessonPreview({ lesson, onClose }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const meta = LESSON_TYPE_META[lesson.type] || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="lesson-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lesson.title} <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        {lesson.type === "video" && (
          lesson.youtube_id ? (
            <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${lesson.youtube_id}?rel=0&modestbranding=1`}
                title={lesson.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <p className="text-sm text-slate-500">Link de YouTube no válido.</p>
          )
        )}

        {lesson.type === "document" && (
          <div className="space-y-3">
            {lesson.doc_view_url && (
              <iframe src={`${backendUrl}${lesson.doc_view_url}`} title={lesson.title} className="w-full h-[60vh] rounded-lg border border-slate-200" />
            )}
            {lesson.doc_url && (
              <a href={lesson.doc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline">
                Abrir documento externo <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}

        {lesson.type === "practice" && (
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">{lesson.body}</div>
            {lesson.attachment_url && (
              <a href={`${backendUrl}${lesson.attachment_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline">
                <Download className="h-3.5 w-3.5" /> Descargar material
              </a>
            )}
          </div>
        )}

        {lesson.type === "quiz" && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Aprueba con {lesson.quiz_pass_pct ?? 70}% · {lesson.quiz_max_attempts ? `${lesson.quiz_max_attempts} intentos` : "Intentos ilimitados"}
            </div>
            {(lesson.quiz_questions || []).map((q, qi) => (
              <div key={q.id} className="p-3 rounded-lg border border-slate-200">
                <div className="text-sm font-medium text-slate-800">P{qi + 1}. {q.prompt}</div>
                {q.type !== "fill_blank" ? (
                  <ul className="mt-2 space-y-1">
                    {q.options.map((o) => (
                      <li key={o.id} className={`text-sm px-2 py-1 rounded ${q.correct_option_ids.includes(o.id) ? "bg-emerald-50 text-emerald-800 font-medium" : "text-slate-600"}`}>
                        {o.text} {q.correct_option_ids.includes(o.id) && "✓"}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-2 text-sm text-slate-600">
                    Respuestas: {q.blanks.map((b) => b.answer).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
